import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { GrooveDialogDirective } from '@groove/angular';

export interface GalleryImage {
  src: string;
  alt: string;
  aspectRatio: string;
  sizes: string;
  /**
   * CSS object-position for the thumbnail's cover crop (default center).
   * For tall portraits in a landscape tile, bias toward the subject
   * (e.g. '50% 20%') so the crop doesn't cut the face. The dialog's
   * full view always shows the whole image regardless.
   */
  position?: string;
}

/**
 * Sequenced accessible image gallery. Renders a responsive thumbnail grid;
 * opens a single shared dialog with previous/next navigation, keyboard
 * support (ArrowLeft / ArrowRight / Escape), and an aria-live position
 * indicator so screen readers announce "Image N of M" as you navigate.
 *
 * Navigation wraps: next on the last image goes to the first, and prev on
 * the first goes to the last — consistent with standard carousel UX.
 *
 * The dialog is a native <dialog> opened via showModal(), which gives free
 * focus trapping and Escape-to-close. Focus returns to the tile that
 * triggered the open on every close path (Escape, backdrop click, or the
 * explicit Close button).
 *
 * Standalone — import this component where you need the gallery. Does NOT
 * change or depend on LightboxImageComponent, which handles standalone
 * single-image use cases on other pages.
 */
@Component({
  selector: 'app-image-gallery',
  imports: [NgOptimizedImage, GrooveDialogDirective],
  templateUrl: './image-gallery.component.html',
  styleUrl: './image-gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageGalleryComponent {
  /** The ordered list of images to display in the gallery grid and viewer. */
  readonly images = input.required<GalleryImage[]>();

  /**
   * When true, wraps each thumbnail tile in the `.groove-img-duotone` CSS
   * treatment (desaturated + duotone colour overlay). The full-view image in
   * the dialog is always shown in native colour regardless of this setting.
   */
  readonly thumbnailDuotone = input<boolean>(false);

  /** Zero-based index of the currently viewed image (changes with navigation). */
  protected readonly currentIndex = signal(0);

  /** The image object for the current index; undefined only if images is empty. */
  protected readonly currentImage = computed<GalleryImage | undefined>(
    () => this.images()[this.currentIndex()],
  );

  /** The native <dialog> element used for the full-size viewer. */
  private readonly dialogRef = viewChild.required<{
    nativeElement: HTMLDialogElement;
  }>('dialog');

  /** The trigger button that opened the dialog; focus returns here on close. */
  private lastTrigger: HTMLElement | null = null;

  /**
   * Open the viewer at the given image index.
   * @param index Zero-based index of the image to display.
   * @param trigger The thumbnail button that was activated; focus will return
   *   to this element when the viewer is closed.
   */
  open(index: number, trigger: HTMLElement): void {
    this.currentIndex.set(index);
    this.lastTrigger = trigger;
    this.dialogRef().nativeElement.showModal();
  }

  /** Close the viewer. Focus returns to the opening trigger via onDialogClose. */
  protected close(): void {
    this.dialogRef().nativeElement.close();
  }

  /**
   * Handler for the native dialog `close` event (fires on Escape, on
   * programmatic close(), and on backdrop-click close). Restores focus to the
   * tile button that opened the viewer.
   */
  protected onDialogClose(): void {
    this.lastTrigger?.focus();
    this.lastTrigger = null;
  }

  /**
   * Close the viewer on any click that is not the photo or a control.
   *
   * This deliberately does NOT test `event.target === event.currentTarget`.
   * The dialog is filled by .gallery-viewer, whose .gallery-stage is a fixed
   * 90vw x 80vh box with the image *contained* inside it — so the empty space
   * around the photo belongs to the stage, not the dialog. Testing for the
   * dialog itself meant a click only dismissed in the thin margin outside the
   * viewer, which read as "clicking outside the image does nothing" for every
   * click directly above, below, or beside the photo (reported 2026-08-11).
   * Anything that is not the image or the control bar is backdrop.
   */
  protected onDialogClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('.gallery-full, .gallery-controls')) {
      return;
    }
    this.close();
  }

  /**
   * Arrow-key navigation inside the dialog. ArrowRight advances, ArrowLeft
   * reverses. Escape is handled natively by the <dialog> element.
   */
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(-1);
    }
  }

  /**
   * Advance by `direction` steps (+1 = next, -1 = prev) with wrapping.
   * Exposed as `protected` so the template can bind it on the nav buttons.
   */
  protected navigate(direction: number): void {
    const len = this.images().length;
    if (len === 0) return;
    this.currentIndex.set((this.currentIndex() + direction + len) % len);
  }
}
